/**
 * The audit trail.
 *
 * Company-admin and above, matching the API exactly. What is in here is not
 * approvals — it is role changes, password resets, anonymisations, payroll runs
 * and exports — so it sits behind a tighter gate than the manager-level screens.
 *
 * Read-only by construction: there is nothing on this page that writes, because
 * there is no endpoint that does. An audit trail the application can edit is not
 * evidence of anything.
 *
 * The action filter is fetched from the server rather than hardcoded here. The
 * vocabulary lives beside the constants that write it, and a list copied into
 * the frontend would quietly fall behind the first time an action was added.
 */
import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Loader2, ScrollText } from 'lucide-react';
import { client } from '../../api/client';
import type { AuditLogEntry, Page } from '../../types';

const PAGE_SIZE = 50;

/** "user.role_changed" reads better as "User · role changed". */
function actionLabel(action: string): string {
  const [group, ...rest] = action.split('.');
  const verb = rest.join('.').replace(/_/g, ' ');
  return `${group.replace(/_/g, ' ')} · ${verb}`;
}

function when(iso: string): string {
  const at = new Date(iso);
  return `${at.toLocaleDateString()} ${at.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

/**
 * Render one changed field.
 *
 * The audit service writes diffs as {field: {from, to}} but plain values for
 * other actions, so both shapes appear in the same column and the row has to
 * cope with either rather than assuming the one it was written against.
 */
function ChangeValue({ value }: { value: unknown }) {
  if (value !== null && typeof value === 'object' && 'from' in (value as object)) {
    const pair = value as { from?: unknown; to?: unknown };
    return (
      <span>
        <span className="text-gray-400 line-through">{String(pair.from ?? '—')}</span>
        {' → '}
        <span className="text-gray-900">{String(pair.to ?? '—')}</span>
      </span>
    );
  }
  if (value !== null && typeof value === 'object') {
    return <code className="text-[11px]">{JSON.stringify(value)}</code>;
  }
  return <span className="text-gray-900">{String(value ?? '—')}</span>;
}

function Details({ entry }: { entry: AuditLogEntry }) {
  const changes = Object.entries(entry.changes ?? {});
  return (
    <tr className="bg-gray-50">
      <td colSpan={5} className="px-4 py-3">
        {entry.note && <p className="mb-2 text-xs text-gray-600">{entry.note}</p>}

        {changes.length === 0 ? (
          <p className="text-xs text-gray-500">No field detail recorded.</p>
        ) : (
          <dl className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
            {changes.map(([field, value]) => (
              <div key={field} className="flex gap-2">
                <dt className="text-gray-500">{field.replace(/_/g, ' ')}</dt>
                <dd>
                  <ChangeValue value={value} />
                </dd>
              </div>
            ))}
          </dl>
        )}

        <p className="mt-2 text-[11px] text-gray-400">
          {entry.entityType}
          {entry.entityId ? ` · ${entry.entityId}` : ''}
          {entry.ipAddress ? ` · from ${entry.ipAddress}` : ''}
        </p>
      </td>
    </tr>
  );
}

export default function AuditLogPage() {
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [offset, setOffset] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);

  const actions = useQuery<string[]>({
    queryKey: ['audit-actions'],
    queryFn: async () => (await client.get('/api/v1/admin/audit-logs/actions')).data,
    // The vocabulary changes only when the application does.
    staleTime: Infinity,
  });

  const entries = useQuery<Page<AuditLogEntry>>({
    queryKey: ['audit-logs', action, entityType, dateFrom, dateTo, offset],
    queryFn: async () =>
      (await client.get('/api/v1/admin/audit-logs', {
        params: {
          limit: PAGE_SIZE,
          offset,
          ...(action ? { action } : {}),
          ...(entityType ? { entityType } : {}),
          ...(dateFrom ? { dateFrom } : {}),
          ...(dateTo ? { dateTo } : {}),
        },
      })).data,
  });

  const reset = (apply: () => void) => {
    apply();
    // Any filter change invalidates the page number: staying on page 3 of a
    // narrower result set shows an empty table that looks like "no records".
    setOffset(0);
  };

  const rows = entries.data?.items ?? [];
  const total = entries.data?.total ?? 0;

  return (
    <div className="space-y-6">
      <section className="card">
        <h2 className="text-sm font-semibold text-gray-900">Audit Trail</h2>
        <p className="mt-1 max-w-2xl text-xs text-gray-500">
          Who did what, and when. Records role changes, password resets,
          payroll runs, exports, document downloads and erasures. Entries are
          written by the actions themselves and cannot be edited or removed.
        </p>

        <div className="mt-3 grid gap-3 sm:grid-cols-4">
          <label className="text-xs text-gray-600">
            Action
            <select
              className="input mt-1"
              value={action}
              onChange={(e) => reset(() => setAction(e.target.value))}
            >
              <option value="">Any action</option>
              {(actions.data ?? []).map((value) => (
                <option key={value} value={value}>
                  {actionLabel(value)}
                </option>
              ))}
            </select>
          </label>

          <label className="text-xs text-gray-600">
            Entity
            <input
              spellCheck={false}
              className="input mt-1"
              value={entityType}
              onChange={(e) => reset(() => setEntityType(e.target.value))}
              placeholder="employee, export…"
            />
          </label>

          <label className="text-xs text-gray-600">
            From
            <input
              className="input mt-1"
              type="date"
              value={dateFrom}
              onChange={(e) => reset(() => setDateFrom(e.target.value))}
            />
          </label>

          <label className="text-xs text-gray-600">
            To
            <input
              className="input mt-1"
              type="date"
              value={dateTo}
              onChange={(e) => reset(() => setDateTo(e.target.value))}
            />
          </label>
        </div>
      </section>

      {entries.isLoading ? (
        <div className="flex justify-center py-10 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : entries.isError ? (
        <div className="card flex items-center justify-between border border-red-200 bg-red-50">
          <p className="text-sm text-red-800">Could not load the audit trail.</p>
          <button type="button" className="btn-ghost" onClick={() => void entries.refetch()}>
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="card flex items-center gap-3 text-sm text-gray-500">
          <ScrollText className="h-4 w-4 text-gray-400" />
          Nothing recorded for these filters.
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-2.5 font-medium">When</th>
                <th className="px-4 py-2.5 font-medium">Who</th>
                <th className="px-4 py-2.5 font-medium">Action</th>
                <th className="px-4 py-2.5 font-medium">Entity</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((entry) => (
                // Fragment, not <>: the shorthand cannot carry a key, and the
                // key belongs on the outermost element the map returns.
                <Fragment key={entry.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                      {when(entry.createdAt)}
                    </td>
                    <td className="px-4 py-2.5 text-gray-900">
                      {entry.actorEmail ?? <span className="text-gray-400">system</span>}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {actionLabel(entry.action)}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500">{entry.entityType}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        type="button"
                        className="btn-ghost text-xs"
                        onClick={() => setOpenId(openId === entry.id ? null : entry.id)}
                      >
                        {openId === entry.id ? (
                          <ChevronDown className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </td>
                  </tr>
                  {openId === entry.id && <Details entry={entry} />}
                </Fragment>
              ))}
            </tbody>
          </table>

          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-2.5 text-xs text-gray-500">
            <span>
              {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-ghost"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn-ghost"
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
